import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FlaskConical,
    GraduationCap,
    Gamepad2,
    Wrench,
    Users,
    Sparkles,
    ShoppingBag,
    ScrollText,
    BookOpen,
    Layers,
    Menu,
    X,
    ArrowRight,
    LogIn,
    LogOut,
    UserCircle,
    Compass,
    Shield,
    ChevronDown,
    Briefcase,
} from 'lucide-react';
import { NAV, ARCHIVE } from '@/constants/testIds';
import { ROADMAP } from '@/constants/testIds';
import { BRAND } from '@/config/site';
import { PRIMARY_ROOMS, ACADEMY_ROOMS, LABS_ROOM, DESKTOP_NAV } from '@/config/navConfig';
import Logo from '@/components/branding/Logo';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { useSiteCopy } from '@/context/SiteCopyContext';
import NotificationBell from '@/components/layout/NotificationBell';
import { useWorkshopFeatures } from '@/workshop/workshopApi';

// Aliases — kept so existing references inside the file remain readable.
const rooms = PRIMARY_ROOMS;
const archiveRooms = ACADEMY_ROOMS;

export default function HUDNav() {
    const [open, setOpen] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [groupOpen, setGroupOpen] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const bannerEnabled = useSiteCopy('banner.enabled');
    const bannerText = useSiteCopy('banner.text');
    const [bannerDismissed, setBannerDismissed] = useState(false);
    useEffect(() => {
        try { setBannerDismissed(sessionStorage.getItem('smi_banner_dismissed') === '1'); } catch {}
        const onDismiss = () => setBannerDismissed(true);
        window.addEventListener('smi:banner-dismissed', onDismiss);
        return () => window.removeEventListener('smi:banner-dismissed', onDismiss);
    }, [location.pathname]);
    const bannerActive = !!bannerEnabled && !!bannerText && !bannerDismissed;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Close menus on route change
    useEffect(() => {
        setOpen(false);
        setArchiveOpen(false);
        setGroupOpen(null);
    }, [location.pathname]);

    // Close archive panel on Esc
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') { setArchiveOpen(false); setGroupOpen(null); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const primaryPaths = PRIMARY_ROOMS.map((r) => r.to);
    const isArchiveActive = archiveRooms.some((r) => !primaryPaths.includes(r.to.split('#')[0]) && location.pathname.startsWith(r.to));

    return (
        <>
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 120, damping: 22, delay: 0.1 }}
                className="fixed left-0 right-0 z-50"
                style={{ top: bannerActive ? 'var(--smi-banner-h, 40px)' : 0 }}
                data-testid={NAV.root}
            >
                <div
                    className={cn(
                        'mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4',
                        scrolled && 'backdrop-blur-xl bg-lab-bg/60 border-b border-white/5',
                        'transition-colors duration-300',
                    )}
                >
                    <NavLink to="/" className="flex items-center gap-3 group" data-testid="brand-link">
                        <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center relative">
                            <Logo size={40} />
                        </div>
                        <div className="flex flex-col leading-tight">
                            <span className="font-display font-semibold tracking-[0.15em] text-white text-[15px]">
                                {BRAND.short}
                            </span>
                            <span className="lab-mono text-[9px] text-lab-mute tracking-[0.24em] uppercase hidden sm:inline">
                                SYNTHETIC MEDIA <span className="text-lab-blue">/</span> INVESTIGATOR
                            </span>
                        </div>
                    </NavLink>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-0.5 glass rounded-full px-1.5 py-1.5">
                        {DESKTOP_NAV.map((entry) => {
                            if (entry.type === 'link') {
                                const room = entry.room;
                                return (
                                    <NavLink
                                        key={room.to}
                                        to={room.to}
                                        end={room.to === '/'}
                                        data-testid={room.testid}
                                        className={({ isActive }) =>
                                            cn(
                                                'relative px-2.5 py-1.5 rounded-full text-[12px] font-medium flex items-center gap-1.5 whitespace-nowrap',
                                                'transition-colors duration-200',
                                                isActive
                                                    ? 'text-black'
                                                    : 'text-white/70 hover:text-white',
                                            )
                                        }
                                    >
                                        {({ isActive }) => (
                                            <>
                                                {isActive && (
                                                    <motion.div
                                                        layoutId="nav-pill"
                                                        className="absolute inset-0 rounded-full bg-gradient-to-r from-lab-blue to-lab-violet"
                                                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                                    />
                                                )}
                                                <room.icon className="w-3 h-3 relative z-10 hidden xl:inline-block" />
                                                <span className="relative z-10">{room.label}</span>
                                            </>
                                        )}
                                    </NavLink>
                                );
                            }
                            if (entry.type === 'academy') {
                                return (
                                    <button
                                        key="academy-trigger"
                                        data-testid={ARCHIVE.trigger}
                                        onClick={() => { setGroupOpen(null); setArchiveOpen((v) => !v); }}
                                        className={cn(
                                            'relative px-2.5 py-1.5 rounded-full text-[12px] font-medium flex items-center gap-1.5 whitespace-nowrap',
                                            'transition-colors duration-200',
                                            isArchiveActive
                                                ? 'text-black'
                                                : 'text-white/70 hover:text-white',
                                        )}
                                        aria-expanded={archiveOpen}
                                        aria-controls="archive-panel"
                                    >
                                        {isArchiveActive && (
                                            <motion.div
                                                layoutId="nav-pill"
                                                className="absolute inset-0 rounded-full bg-gradient-to-r from-lab-blue to-lab-violet"
                                                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                            />
                                        )}
                                        <GraduationCap className="w-3 h-3 relative z-10 hidden xl:inline-block" />
                                        <span className="relative z-10">Academy</span>
                                        <span className="hidden xl:inline-flex lab-mono text-[8px] px-1.5 py-[1px] rounded-full bg-lab-emerald/20 text-lab-emerald tracking-wider relative z-10 ml-0.5 border border-lab-emerald/30">
                                            FREE
                                        </span>
                                        <motion.span
                                            className="w-1 h-1 rounded-full bg-lab-emerald relative z-10 ml-0.5"
                                            animate={{ opacity: [1, 0.4, 1] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        />
                                    </button>
                                );
                            }
                            const groupActive = entry.rooms.some((r) => location.pathname.startsWith(r.to));
                            const isOpen = groupOpen === entry.id;
                            return (
                                <div key={entry.id} className="relative">
                                    <button
                                        data-testid={entry.testid}
                                        onClick={() => { setArchiveOpen(false); setGroupOpen((g) => (g === entry.id ? null : entry.id)); }}
                                        aria-expanded={isOpen}
                                        className={cn(
                                            'relative px-2.5 py-1.5 rounded-full text-[12px] font-medium flex items-center gap-1.5 whitespace-nowrap',
                                            'transition-colors duration-200',
                                            groupActive ? 'text-black' : 'text-white/70 hover:text-white',
                                        )}
                                    >
                                        {groupActive && (
                                            <motion.div
                                                layoutId="nav-pill"
                                                className="absolute inset-0 rounded-full bg-gradient-to-r from-lab-blue to-lab-violet"
                                                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                            />
                                        )}
                                        <entry.icon className="w-3 h-3 relative z-10 hidden xl:inline-block" />
                                        <span className="relative z-10">{entry.label}</span>
                                        <ChevronDown className={cn('w-3 h-3 relative z-10 transition-transform', isOpen && 'rotate-180')} />
                                    </button>
                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -6 }}
                                                transition={{ duration: 0.15 }}
                                                data-testid={`${entry.testid}-panel`}
                                                className="absolute top-full left-0 mt-3 min-w-[220px] glass-strong rounded-2xl p-2 z-50"
                                            >
                                                {entry.rooms.map((r) => (
                                                    <NavLink
                                                        key={r.to}
                                                        to={r.to}
                                                        data-testid={r.testid}
                                                        onClick={() => setGroupOpen(null)}
                                                        className={({ isActive }) =>
                                                            cn(
                                                                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-[12px] transition-colors',
                                                                isActive
                                                                    ? 'bg-gradient-to-r from-lab-blue/20 to-lab-violet/20 text-white'
                                                                    : 'text-white/70 hover:bg-white/5 hover:text-white',
                                                            )
                                                        }
                                                    >
                                                        <span className="lab-mono text-[9px] text-lab-blue tracking-[0.16em]">{r.code}</span>
                                                        <r.icon className="w-3.5 h-3.5" />
                                                        <span className="font-medium">{r.label}</span>
                                                    </NavLink>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </nav>

                    {/* Auth chip — desktop */}
                    <AuthChip />

                    {/* Mobile toggle */}
                    <button
                        className="lg:hidden w-10 h-10 glass rounded-xl flex items-center justify-center"
                        onClick={() => setOpen((v) => !v)}
                        data-testid={NAV.menuToggle}
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-4 h-4 text-white" /> : <Menu className="w-4 h-4 text-white" />}
                    </button>
                </div>
            </motion.header>

            {/* Archive HUD panel — desktop */}
            <AnimatePresence>
                {archiveOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 hidden lg:block"
                        onClick={() => setArchiveOpen(false)}
                    >
                        {/* Scrim */}
                        <div className="absolute inset-0 bg-lab-bg/50 backdrop-blur-md" />

                        <motion.div
                            id="archive-panel"
                            data-testid={ARCHIVE.panel}
                            onClick={(e) => e.stopPropagation()}
                            initial={{ y: -16, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -12, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 260, damping: 26 }}
                            className="absolute top-20 left-1/2 -translate-x-1/2 w-[min(900px,92vw)] glass-strong rounded-3xl p-6 relative overflow-hidden noise-grain"
                        >
                            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-lab-blue/20 blur-3xl pointer-events-none" />
                            <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-lab-violet/20 blur-3xl pointer-events-none" />
                            <div className="relative">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="lab-overline inline-flex items-center gap-2">
                                        <GraduationCap className="w-3 h-3" />
                                        THE ACADEMY · FREE ACCESS TIER
                                    </div>
                                    <button
                                        onClick={() => setArchiveOpen(false)}
                                        className="w-8 h-8 rounded-full glass-soft flex items-center justify-center hover:bg-white/10 transition-colors"
                                        aria-label="Close archive"
                                    >
                                        <X className="w-3.5 h-3.5 text-white" />
                                    </button>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                    {archiveRooms.map((r) => (
                                        <Link
                                            key={r.to}
                                            to={r.to}
                                            data-testid={r.panelTestid}
                                            onClick={() => setArchiveOpen(false)}
                                            className="group relative glass rounded-2xl p-5 flex items-start gap-4 hover:border-white/25 transition-colors overflow-hidden"
                                        >
                                            <div
                                                className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl opacity-25 transition-opacity group-hover:opacity-50"
                                                style={{ background: r.color }}
                                            />
                                            <div
                                                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 relative"
                                                style={{
                                                    background: `radial-gradient(circle at 30% 30%, ${r.color}33, rgba(15,15,25,0.9))`,
                                                    border: `1px solid ${r.color}55`,
                                                }}
                                            >
                                                <r.icon className="w-5 h-5" style={{ color: r.color }} />
                                            </div>
                                            <div className="relative flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <div className="lab-mono text-[10px]" style={{ color: r.color }}>
                                                        ROOM.{r.code}
                                                    </div>
                                                    {r.badge && (
                                                        <span className="lab-mono text-[8px] px-1.5 py-[1px] rounded-full bg-lab-emerald/20 text-lab-emerald tracking-wider">
                                                            {r.badge}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="font-display text-lg text-white mt-0.5 leading-tight">
                                                    {r.label}
                                                </div>
                                                <div className="text-lab-mute text-xs mt-1">{r.subtitle}</div>
                                            </div>
                                            <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all mt-1" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mobile drawer */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 lg:hidden"
                    >
                        <div
                            className="absolute inset-0 bg-lab-bg/85 backdrop-blur-xl"
                            onClick={() => setOpen(false)}
                        />
                        <motion.nav
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 120, damping: 22 }}
                            className="absolute top-20 left-4 right-4 glass-strong rounded-2xl p-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto"
                            data-testid="mobile-drawer"
                        >
                            {/* Auth strip — always at the top so sign-in is never buried. */}
                            <MobileAuthStrip onNavigate={() => setOpen(false)} />
                            <div className="my-2 border-t border-white/5" />
                            <div className="lab-mono text-[10px] text-lab-mute px-4 pt-1 pb-2 flex items-center gap-2">
                                <Compass className="w-3 h-3" />
                                ROOMS
                            </div>
                            {[...rooms, LABS_ROOM].map((room) => (
                                <NavLink
                                    key={room.to}
                                    to={room.to}
                                    end={room.to === '/'}
                                    data-testid={room.testid}
                                    className={({ isActive }) =>
                                        cn(
                                            'flex items-center gap-4 rounded-xl px-4 py-4 group',
                                            'transition-colors duration-200',
                                            isActive
                                                ? 'bg-gradient-to-r from-lab-blue/20 to-lab-violet/20 text-white'
                                                : 'text-white/70 hover:bg-white/5 hover:text-white',
                                        )
                                    }
                                >
                                    <span className="lab-mono text-[10px] text-lab-blue tracking-[0.2em]">
                                        {room.code}
                                    </span>
                                    <room.icon className="w-4 h-4" />
                                    <span className="font-display font-medium">{room.label}</span>
                                    {room.badge && (
                                        <span className="lab-mono text-[8px] px-1.5 py-[1px] rounded-full bg-lab-violet/20 text-lab-violet border border-lab-violet/40 shadow-[0_0_10px_rgba(168,85,247,0.45)] animate-pulse">{room.badge}</span>
                                    )}
                                </NavLink>
                            ))}
                            <div className="my-2 border-t border-white/5" />
                            <div className="lab-mono text-[10px] text-lab-mute px-4 pt-1 pb-2 flex items-center gap-2">
                                <GraduationCap className="w-3 h-3" />
                                ACADEMY · FREE ACCESS
                            </div>
                            {archiveRooms.map((r) => (
                                <NavLink
                                    key={r.to}
                                    to={r.to}
                                    data-testid={r.testid}
                                    className={({ isActive }) =>
                                        cn(
                                            'flex items-center gap-4 rounded-xl px-4 py-4 group',
                                            'transition-colors duration-200',
                                            isActive
                                                ? 'bg-gradient-to-r from-lab-blue/20 to-lab-violet/20 text-white'
                                                : 'text-white/70 hover:bg-white/5 hover:text-white',
                                        )
                                    }
                                >
                                    <span
                                        className="lab-mono text-[10px] tracking-[0.2em]"
                                        style={{ color: r.color }}
                                    >
                                        {r.code}
                                    </span>
                                    <r.icon className="w-4 h-4" />
                                    <span className="font-display font-medium">{r.label}</span>
                                </NavLink>
                            ))}
                        </motion.nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

/* ─────────────────────────────────────────────────────────
   Auth chip — sign-in button when signed out, avatar+desk link when in
   ───────────────────────────────────────────────────────── */
function AuthChip() {
    const { user, loading, signIn, signOut } = useAuth();
    const caseRooms = useWorkshopFeatures();

    if (loading) {
        return (
            <div className="hidden lg:flex items-center gap-2 glass rounded-full px-3 py-2 lab-mono text-[10px] text-lab-mute" data-testid="auth-chip-loading">
                <span className="w-2 h-2 rounded-full bg-lab-blue animate-pulse" />
                CHECKING
            </div>
        );
    }

    if (!user) {
        return (
            <div className="hidden lg:flex items-center gap-2">
                <Link
                    to="/automation-center/services"
                    data-testid="nav-discuss-project"
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-lab-cyan/50 text-lab-cyan lab-mono text-[10px] tracking-[0.12em] hover:bg-lab-cyan/10 transition-colors"
                >
                    DISCUSS YOUR PROJECT
                </Link>
                <button
                    onClick={signIn}
                    data-testid="auth-signin-button"
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2 bg-gradient-to-r from-lab-blue to-lab-violet text-black text-xs font-medium hover:scale-[1.03] active:scale-[0.97] transition-transform"
                >
                    <LogIn className="w-3.5 h-3.5" />
                    Sign in
                </button>
            </div>
        );
    }

    return (
        <div className="hidden lg:flex items-center gap-2" data-testid="auth-chip-user">
            <NotificationBell />
            {caseRooms?.enabled && (
                <Link
                    to="/workshop"
                    data-testid="auth-case-rooms-link"
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 border border-white/15 hover:border-white/30 transition-colors lab-mono text-[10px] text-lab-blue tracking-wider"
                >
                    <Briefcase className="w-3 h-3" /> CASE ROOMS
                </Link>
            )}
            {user.role === 'admin' && (
                <Link
                    to="/admin"
                    data-testid="auth-admin-link"
                    className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 border border-lab-emerald/40 bg-lab-emerald/10 hover:bg-lab-emerald/20 transition-colors lab-mono text-[10px] text-lab-emerald tracking-wider"
                    aria-label="Admin console"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-lab-emerald animate-pulse" />
                    ADMIN
                </Link>
            )}
            <Link
                to="/desk"
                data-testid="auth-desk-link"
                className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 hover:border-white/30 transition-colors border border-white/10"
            >
                {user.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                    <UserCircle className="w-5 h-5 text-lab-blue" />
                )}
                <span className="text-white text-xs font-medium max-w-[90px] truncate">
                    {user.name?.split(' ')[0] || 'Desk'}
                </span>
                <span className="lab-mono text-[8px] px-1.5 py-[1px] rounded-full bg-lab-emerald/15 text-lab-emerald border border-lab-emerald/30 uppercase tracking-wider">
                    {user.tier || 'observer'}
                </span>
            </Link>
            <button
                onClick={signOut}
                data-testid="auth-signout-button"
                className="lab-mono text-[9px] text-lab-mute hover:text-white transition-colors px-2"
                aria-label="Sign out"
            >
                OUT
            </button>
        </div>
    );
}

/* ─────────────────────────────────────────────────────────
   Mobile auth strip — top of the mobile drawer.
   Ensures SIGN IN (signed out) / DESK + ADMIN + SIGN OUT (signed in)
   are always reachable and never buried.
   ───────────────────────────────────────────────────────── */
function MobileAuthStrip({ onNavigate }) {
    const { user, loading, signIn, signOut } = useAuth();
    const caseRooms = useWorkshopFeatures();

    if (loading) {
        return (
            <div
                className="flex items-center gap-2 glass rounded-xl px-4 py-3 lab-mono text-[10px] text-lab-mute"
                data-testid="mobile-auth-loading"
            >
                <span className="w-2 h-2 rounded-full bg-lab-blue animate-pulse" />
                CHECKING SESSION
            </div>
        );
    }

    if (!user) {
        return (
            <button
                onClick={() => { onNavigate?.(); signIn(); }}
                data-testid="mobile-auth-signin"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 bg-gradient-to-r from-lab-blue to-lab-violet text-black font-medium text-sm active:scale-[0.98] transition-transform"
            >
                <LogIn className="w-4 h-4" />
                Sign in · Clearance
            </button>
        );
    }

    return (
        <div className="flex flex-col gap-1" data-testid="mobile-auth-user">
            <div className="flex items-center justify-between px-1 pb-1">
                <span className="lab-mono text-[9px] text-lab-mute tracking-[0.2em]">INBOX</span>
                <NotificationBell mobile onNavigate={onNavigate} />
            </div>
            <Link
                to="/desk"
                data-testid="mobile-auth-desk"
                onClick={onNavigate}
                className="flex items-center gap-3 rounded-xl px-4 py-3 bg-gradient-to-r from-lab-blue/15 to-lab-violet/15 border border-white/10 hover:border-white/25 transition-colors"
            >
                {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                    <UserCircle className="w-6 h-6 text-lab-blue" />
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-white text-sm font-medium truncate">
                        {user.name || 'Investigator'}
                    </div>
                    <div className="lab-mono text-[9px] text-lab-mute uppercase tracking-wider truncate">
                        Desk · {user.tier || 'observer'}
                    </div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/50" />
            </Link>
            {caseRooms?.enabled && (
                <Link
                    to="/workshop"
                    data-testid="mobile-auth-case-rooms"
                    onClick={onNavigate}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 border border-white/10 hover:bg-white/5 transition-colors"
                >
                    <Briefcase className="w-4 h-4 text-lab-blue" />
                    <span className="lab-mono text-[11px] text-lab-blue tracking-wider flex-1">CASE ROOMS</span>
                    <ArrowRight className="w-4 h-4 text-white/50" />
                </Link>
            )}
            {user.role === 'admin' && (
                <Link
                    to="/admin"
                    data-testid="mobile-auth-admin"
                    onClick={onNavigate}
                    className="flex items-center gap-3 rounded-xl px-4 py-3 border border-lab-emerald/40 bg-lab-emerald/10 hover:bg-lab-emerald/15 transition-colors"
                >
                    <Shield className="w-4 h-4 text-lab-emerald" />
                    <span className="lab-mono text-[11px] text-lab-emerald tracking-wider flex-1">
                        ADMIN CONSOLE
                    </span>
                    <ArrowRight className="w-4 h-4 text-lab-emerald/70" />
                </Link>
            )}
            <button
                onClick={() => { onNavigate?.(); signOut(); }}
                data-testid="mobile-auth-signout"
                className="flex items-center gap-3 rounded-xl px-4 py-3 hover:bg-white/5 transition-colors text-left"
            >
                <LogOut className="w-4 h-4 text-lab-mute" />
                <span className="lab-mono text-[11px] text-lab-mute tracking-wider">
                    Sign out
                </span>
            </button>
        </div>
    );
}
